import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "General", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Inquiry received — our office will respond shortly.");
      setForm({ name: "", email: "", company: "", interest: "General", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="contact" className="py-20 lg:py-28 surface-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-semibold mb-3">Contact</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-emerald-50 uppercase tracking-tight">
            Institutional & <span className="gold-gradient-text">Enterprise Inquiries</span>
          </h2>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="rounded-2xl border border-emerald-900/60 surface-panel p-7 lg:p-10 grid sm:grid-cols-2 gap-5"
        >
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Name</label>
            <Input required value={form.name} onChange={set("name")} data-testid="contact-name" className="mt-2 surface-input border-emerald-900 text-emerald-50" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Email</label>
            <Input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className="mt-2 surface-input border-emerald-900 text-emerald-50" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Company</label>
            <Input value={form.company} onChange={set("company")} data-testid="contact-company" className="mt-2 surface-input border-emerald-900 text-emerald-50" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Interest</label>
            <select value={form.interest} onChange={set("interest")} data-testid="contact-interest" className="mt-2 w-full h-10 rounded-md surface-input border border-emerald-900 text-emerald-50 px-3 text-sm">
              <option>General</option>
              <option>Luchii AI Models</option>
              <option>Frasberg AI Models</option>
              <option>Investment / Partnership</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-widest text-slate-400">Message</label>
            <Textarea required rows={4} value={form.message} onChange={set("message")} data-testid="contact-message" className="mt-2 surface-input border-emerald-900 text-emerald-50" />
          </div>
          <div className="sm:col-span-2">
            <Button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-full h-12 tracking-wide hover-emerald-glow border border-emerald-400/30"
            >
              {loading ? "Sending…" : <>Submit Inquiry <Send className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
